import React from "react";
import styled from "styled-components";
import moment from "moment";
import { Button, ControlLabel, FormControl, FormGroup, Radio } from "components/bootstrap";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { Pluralize, Select, TimeUnitInput } from "components/common";
import { getValueFromInput } from "util/FormsUtils";
import withTelemetry from "logic/telemetry/withTelemetry";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import withLocation from "routing/withLocation";
const formatEventDefinitions = (eventDefinitions) => eventDefinitions.map((definition) => ({ label: definition.title, value: definition.id }));
const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const FieldsetRule = styled.fieldset`
  margin-bottom: 10px;
`;
const ComplexRadio = styled(Radio)`
  input[type='radio'] {
    bottom: 10px;
  }

  .form-inline {
    margin-bottom: 0;
  }
`;
const InlineSelect = styled.div`
  display: inline-block;
  width: 100%;
  vertical-align: middle;
`;
const InlineFormGroup = styled.div`
  margin-bottom: 15px;

  p {
    font-size: 14px;
    font-weight: initial;
  }

  .form-group:first-child, .form-group+*, *+.form-group {
    margin-right: 10px;
  }
}
`;
class CorrelationRule extends React.Component {
  constructor(props) {
    var _a, _b;
    super(props);
    this.propagateChange = (ruleUpdate = {}) => {
      const { correlationRule, onChange } = this.props;
      const nextRule = { ...correlationRule, ...ruleUpdate };
      onChange(correlationRule.id, nextRule);
    };
    this.handleEventDefinitionChange = (nextEventDefinition) => {
      this.props.sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_EVENT_SELECTED, {
        app_pathname: getPathnameWithoutId(this.props.location.pathname),
        app_section: "event-definition-condition",
        app_action_value: "event-select"
      });
      this.propagateChange({ event_creator_equals: nextEventDefinition });
    };
    this.handleOccurrenceChange = (event) => {
      const value = getValueFromInput(event.target);
      this.props.sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_OCCURRENCE_TIME_CHANGED, {
        app_pathname: getPathnameWithoutId(this.props.location.pathname),
        app_section: "event-definition-condition",
        app_action_value: "occurrence-input",
        occurrence: value
      });
      this.propagateChange({ occurrence: value });
      this.setState({ occurrence: value });
    };
    this.handleStateLogicChange = (event) => {
      const { occurrence, notForPeriod } = this.state;
      const value = getValueFromInput(event.target);
      let ruleUpdate;
      switch (value) {
        case "not_and":
          this.props.sendTelemetry(
            TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_NO_OCCURRENCE_FOLLOWED_BY_EVENT_SELECTED,
            {
              app_pathname: getPathnameWithoutId(this.props.location.pathname),
              app_section: "event-definition-condition",
              app_action_value: "occurrenceFollowedByEvent-radio-button"
            }
          );
          ruleUpdate = { state_logic: value, occurrence: void 0, not_for_period: void 0 };
          break;
        case "not_for":
          this.props.sendTelemetry(
            TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_NO_OCCURRENCE_IN_THE_NEXT_SELECTED,
            {
              app_pathname: getPathnameWithoutId(this.props.location.pathname),
              app_section: "event-definition-condition",
              app_action_value: "noOccurrenceInTheNext-radio-button"
            }
          );
          ruleUpdate = { state_logic: value, occurrence: void 0, not_for_period: notForPeriod };
          break;
        default:
          ruleUpdate = { state_logic: void 0, occurrence, not_for_period: void 0 };
      }
      this.propagateChange(ruleUpdate);
    };
    this.handleNotForPeriodChange = (nextValue, nextUnit) => {
      this.props.sendTelemetry(
        TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.CORRELATION_NO_OCCURRENCE_IN_THE_NEXT_UNIT_CHANGED,
        {
          app_pathname: getPathnameWithoutId(this.props.location.pathname),
          app_section: "event-definition-condition",
          app_action_value: "noOccurrenceInTheNextUnit-select",
          new_unit: nextUnit
        }
      );
      const duration = moment.duration(Math.max(nextValue, 1), nextUnit).toISOString();
      this.propagateChange({ not_for_period: duration });
      this.setState({
        notForPeriodDuration: nextValue,
        notForPeriodUnit: nextUnit,
        notForPeriod: duration
      });
    };
    this.handleRemove = () => {
      const { correlationRule, onRemove } = this.props;
      onRemove(correlationRule.id);
    };
    this.renderFirstRuleOptions = (correlationRule) => {
      const { occurrence } = this.state;
      return /* @__PURE__ */ React.createElement(InlineFormGroup, { className: "form-inline" }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, "Should occur at least")), /* @__PURE__ */ React.createElement(
        FormControl,
        {
          id: `${correlationRule.id}-occurrence`,
          componentClass: "input",
          type: "number",
          name: "occurrence",
          value: occurrence,
          onChange: this.handleOccurrenceChange
        }
      ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, /* @__PURE__ */ React.createElement(Pluralize, { singular: "time", plural: "times", value: occurrence }), ".")));
    };
    this.renderRegularRuleOptions = (correlationRule) => {
      const { occurrence, notForPeriodDuration, notForPeriodUnit } = this.state;
      return /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Should"), /* @__PURE__ */ React.createElement(
        ComplexRadio,
        {
          id: `${correlationRule.id}-regular-rule-type`,
          name: `${correlationRule.id}-rule-type`,
          value: "regular",
          checked: correlationRule.state_logic === void 0 || correlationRule.state_logic === null,
          onChange: this.handleStateLogicChange
        },
        /* @__PURE__ */ React.createElement(InlineFormGroup, { className: "form-inline" }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, "Occur at least")), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
          FormControl,
          {
            id: `${correlationRule.id}-occurrence`,
            componentClass: "input",
            type: "number",
            name: "occurrence",
            value: occurrence,
            disabled: correlationRule.state_logic !== void 0 && correlationRule.state_logic !== null,
            onChange: this.handleOccurrenceChange
          }
        )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, /* @__PURE__ */ React.createElement(Pluralize, { singular: "time", plural: "times", value: occurrence }), ".")))
      ), /* @__PURE__ */ React.createElement(
        Radio,
        {
          id: `${correlationRule.id}-not-and-rule-type`,
          name: `${correlationRule.id}-rule-type`,
          value: "not_and",
          checked: correlationRule.state_logic === "not_and",
          onChange: this.handleStateLogicChange
        },
        "Not occur, and it is followed by another Event"
      ), /* @__PURE__ */ React.createElement(
        ComplexRadio,
        {
          id: `${correlationRule.id}-not-for-rule-type`,
          name: `${correlationRule.id}-rule-type`,
          value: "not_for",
          checked: correlationRule.state_logic === "not_for",
          onChange: this.handleStateLogicChange
        },
        /* @__PURE__ */ React.createElement(InlineFormGroup, { className: "form-inline" }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, "Not occur in the next")), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
          TimeUnitInput,
          {
            update: this.handleNotForPeriodChange,
            value: notForPeriodDuration,
            unit: notForPeriodUnit,
            units: TIME_UNITS,
            enabled: correlationRule.state_logic === "not_for",
            clearable: true,
            hideCheckbox: true
          }
        )))
      ));
    };
    this.renderRuleOptions = (number, correlationRule) => {
      if (number === 1) {
        return this.renderFirstRuleOptions(correlationRule);
      }
      return this.renderRegularRuleOptions(correlationRule);
    };
    const notForPeriod = (_a = props.correlationRule.not_for_period) != null ? _a : "PT1S";
    const { duration, unit } = extractDurationAndUnit(notForPeriod, TIME_UNITS);
    this.state = {
      occurrence: (_b = props.correlationRule.occurrence) != null ? _b : 1,
      notForPeriod,
      notForPeriodDuration: duration,
      notForPeriodUnit: unit
    };
  }
  render() {
    const { number, correlationRule, eventDefinitions } = this.props;
    return /* @__PURE__ */ React.createElement(FieldsetRule, null, /* @__PURE__ */ React.createElement("legend", null, "Event #", number, number !== 1 && /* @__PURE__ */ React.createElement("span", { className: "pull-right" }, /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", bsStyle: "danger", onClick: this.handleRemove }, "Delete"))), /* @__PURE__ */ React.createElement(InlineFormGroup, { className: "form-inline" }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(FormControl.Static, null, "Event from")), /* @__PURE__ */ React.createElement(InlineSelect, null, /* @__PURE__ */ React.createElement(
      Select,
      {
        id: `${correlationRule.id}-event-definition`,
        onChange: this.handleEventDefinitionChange,
        options: formatEventDefinitions(eventDefinitions),
        value: correlationRule.event_creator_equals,
        required: true
      }
    ))), this.renderRuleOptions(number, correlationRule));
  }
}
export default withLocation(withTelemetry(CorrelationRule));
